import React from 'react';
import { format } from 'date-fns';
import { Department } from '../../types/organization.types';
import { useUpdateDepartment } from '../../hooks/useOrgQueries';
import { useToast } from '../../hooks/useToast';
import { cn } from '../../lib/cn';

// Same date rules as RegisterPeek/TaskPeek: local time for timestamps.
const stampLocal = (iso: string | null | undefined): string | null =>
  iso ? format(new Date(iso), 'MMM d, yyyy').toUpperCase() : null;

interface DepartmentPeekProps {
  department: Department;
  onClose: () => void;
  onEdit: (id: string) => void;
  onDelete: (department: Department) => void;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="cap-label">{label}</div>
    <div className="mt-0.5 text-scale-1 text-ink">{children}</div>
  </div>
);

export const DepartmentPeek: React.FC<DepartmentPeekProps> = ({ department, onClose, onEdit, onDelete }) => {
  const update = useUpdateDepartment();
  const toast = useToast();

  const onSaveError = () => toast.error('Save failed');
  const teamCompany = [department.teamName, department.companyName].filter(Boolean).join(' · ');
  const updatedStamp = stampLocal(department.updatedAt);

  return (
    <aside aria-label="Department peek" className="border-l border-ink bg-paper-raised p-5">
      <div className="flex items-baseline justify-between">
        <span className="cap-label">DEPARTMENT</span>
        <span className="flex gap-3">
          <button type="button" onClick={() => onEdit(department.id)} className="cap-label cursor-pointer !text-accent hover:underline">
            Edit ↗
          </button>
          <button type="button" onClick={() => onDelete(department)} className="cap-label cursor-pointer text-ink-muted hover:!text-sev-critical">
            Delete
          </button>
          <button type="button" aria-label="Close peek" onClick={onClose} className="cursor-pointer text-scale-0 text-ink-muted hover:text-ink">
            ✕
          </button>
        </span>
      </div>

      <h2 className="mt-1.5 font-serif text-scale-4 font-medium text-ink">{department.name}</h2>
      {teamCompany && <p className="mb-3 text-scale-1 text-ink-muted">{teamCompany}</p>}
      <div className="border-b border-ink" />

      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 py-3">
        <Field label="Members">
          <span className="font-mono">{department.usersCount}</span>
        </Field>
        <Field label="Status">
          <button
            type="button"
            role="switch"
            aria-checked={department.isActive}
            aria-label="Active"
            disabled={update.isPending}
            onClick={() =>
              update.mutate(
                { id: department.id, data: { isActive: !department.isActive } },
                { onError: onSaveError },
              )
            }
            className="flex cursor-pointer items-center gap-2 disabled:cursor-default"
          >
            <span aria-hidden className={cn('h-3 w-3 border border-ink', department.isActive && 'bg-ink')} />
            <span className="text-scale-1 text-ink">{department.isActive ? 'Active' : 'Inactive'}</span>
          </button>
        </Field>
      </div>

      {department.description && (
        <p className="border-t border-rule-faint py-3 text-scale-1 text-ink-muted">{department.description}</p>
      )}

      <div className="cap-label mt-4 border-b border-ink pb-0.5">History</div>
      <div className="pt-1.5 font-serif text-scale-1 italic text-ink-muted">
        <div>Recorded {stampLocal(department.createdAt)}</div>
        {updatedStamp && <div>Updated {updatedStamp}</div>}
      </div>
    </aside>
  );
};
